import { FC, useState, ChangeEvent, useEffect } from "react"
import styled from "styled-components"
import { Color } from "@lib/types"
import { LazyMotion, m } from "framer-motion"
const loadFeatures = () => import("@lib/framer-motion").then(res => res.default)

import { Button } from "@components/ui"
import { ColorFilter } from "./ColorFilter"
import { ButtonFilter } from "./ButtonFilter"
import DropdownSvg from "@svg/chevron-thin.svg"

interface Props {
  filters: {
    color: Color[]
    fabricTexture: string[]
    fabricThickness: string[]
    size: string[]
  }
  state: any
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void
  clearFilters: () => void
}

export const Filter: FC<Props> = ({
  children,
  filters,
  state,
  handleChange,
  clearFilters,
}) => {
  const [isActive, setIsActive] = useState(false)

  const applyCustomOrder = (arr: any, desiredOrder: any) => {
    const orderForIndexVals = desiredOrder.slice(0).reverse();
    arr.sort((a: any, b: any) => {
      const aIndex = -orderForIndexVals.indexOf(a);
      const bIndex = -orderForIndexVals.indexOf(b);
      return aIndex - bIndex;
    });
  }

  useEffect(() => {
    const customOrderThickness = ["Light", "Medium", "Heavy"];
    applyCustomOrder(filters.fabricThickness, customOrderThickness);
    const customOrderTexture = ["Smooth", "Medium", "Textured"];
    applyCustomOrder(filters.fabricTexture, customOrderTexture);
  }, [])

  return (
    <StyledFilter>
      <Header>
        <FilterButton type="button" onClick={() => setIsActive(!isActive)}>
          Filters
          <StyledDropdownSvg $isActive={isActive} />
        </FilterButton>
        {children}
      </Header>
      <LazyMotion strict features={loadFeatures}>
        <Body
          animate={{
            height: isActive ? "auto" : "0",
          }}
          initial={{ height: 0 }}
          transition={{ duration: 0.25 }}
        >
          <BodyInner>
            <Filters>
              {filters.color.length > 0 && (
                <ColorFilter
                  colors={filters.color}
                  state={state}
                  onChange={handleChange}
                />
              )}
              {filters.size.length > 0 && (
                <ButtonFilter
                  name="size"
                  title="Size"
                  filters={filters.size}
                  state={state}
                  onChange={handleChange}
                />
              )}
              {filters.fabricTexture.length > 0 && (
                <ButtonFilter
                  name="fabricTexture"
                  title="Fabric texture"
                  filters={filters.fabricTexture}
                  state={state}
                  onChange={handleChange}
                />
              )}
              {filters.fabricThickness.length > 0 && (
                <ButtonFilter
                  name="fabricThickness"
                  title="Fabric thickness"
                  filters={filters.fabricThickness}
                  state={state}
                  onChange={handleChange}
                />
              )}
            </Filters>
            <ClearFilterButton
              buttonTheme="ghostBlackReverse"
              title="Clear Filters"
              onClick={clearFilters}
            >
              Clear filters
            </ClearFilterButton>
          </BodyInner>
        </Body>
      </LazyMotion>
    </StyledFilter>
  )
}

interface SVGProps {
  $isActive: boolean
}

const StyledFilter = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.color.greyLight};
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 0;
  border-top: 1px solid ${({ theme }) => theme.color.greyLight};

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    padding: 0 0 1.5rem;
    border-top: none;
    border-bottom: 1px solid ${({ theme }) => theme.color.greyLight};
  }
`

const Body = styled(m.div)`
  overflow-y: hidden;
  height: auto;
`

const BodyInner = styled.div`
  padding: 2.2rem 0 4.1rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    padding: 2.5rem 0 4.8rem;
  }
`

const Filters = styled.div`
  display: grid;
  grid-row-gap: 2.8rem;
  margin: 0 0 3.9rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    grid-template-columns: repeat(2, calc(50% - 2.2rem));
    grid-gap: 2.5rem 4.4rem;
    margin: 0 0 4.8rem;
  }
`

const ClearFilterButton = styled(Button)`
  letter-spacing: 1.85px;
  font-weight: 500;
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  padding: 1.4rem 1.2rem;
  border: 1px solid #d8d8d8;
  border-radius: 3px;
  background: ${({ theme }) => theme.color.white};
  font-size: 1.5rem;
  line-height: 1.4rem;
`

const StyledDropdownSvg = styled(DropdownSvg)<SVGProps>`
  margin-left: 1.4rem;
  pointer-events: none;
  width: 1rem;
  transform: ${({ $isActive }) => ($isActive ? "rotate(180deg)" : null)};
  transition: transform 0.25s ease-out;

  path {
    opacity: 1;
  }
`
